import { useRef } from 'react';

import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { useCloseWithESC } from '@revenuemarket/react';
import { Breadcrumb, Typography } from 'antd';

import { CategoryTreeItem } from '~apis/categories';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { css } from '~libs/emotion';
import { isSystemCategory } from '~utils/category';

import Info from './CategoryLabelInfo';
import CategoryLabelRemoveModal from './CategoryLabelRemoveModal';
import useLabelManage from './GroupTreeSelect/hooks/useLabelManage';
import NewInfo from './NewInfo';
import LabelCreateButton from './common/component/LabelCreateButton';
import LabelRemoveButton from './common/component/LabelRemoveButton';
import PanelHeader from './common/component/PanelHeader';
import { PanelWrapper } from './common/styles';
import useCategoryLabelTreeStore from './store/categoryLabelTreeStore';

type Props = {
  activeKey: string;
  setActiveItem: (item?: FlattenTreeResult<CategoryTreeItem>) => void;
  onNewCategoryClick?: (key: string) => void;
  viewType?: 'new' | 'info';
};

const Header: React.FC<
  CategoryTreeItem & {
    namePath: string;
    keyPath: string;
    viewStatus: 'new' | 'info';

    onBreadcrumbClick?: (key: string) => void;
  }
> = ({ namePath, keyPath, type, isCardPayment, viewStatus, onBreadcrumbClick }) => {
  const breadcrumbItems =
    viewStatus === 'info'
      ? namePath.split('.').map((key, index) => {
          return {
            key: keyPath.split('.')[index],
            onClick: () => {
              onBreadcrumbClick?.(keyPath.split('.')[index]);
            },
            title: key,
            className: css`
              cursor: pointer;
            `,
          };
        })
      : [
          ...namePath?.split('.').map((key, index) => {
            return {
              key: keyPath.split('.')[index],
              onClick: () => {
                onBreadcrumbClick?.(keyPath.split('.')[index]);
              },
              title: key,
              className: css`
                cursor: pointer;
              `,
            };
          }),
          {
            key: 'new',
            title: '생성중..',
          },
        ];

  return (
    <Flex direction="column" gap={8}>
      <Breadcrumb items={breadcrumbItems} />
      <Flex gap={8} align="center">
        {isSystemCategory(type) && viewStatus === 'info' && (
          <span
            style={{
              width: 'fit-content',
              padding: 0,
              borderRadius: 5,
              backgroundColor: Colors.grayOpacity07,
            }}
          >
            <Typography.Text
              style={{
                padding: '0 4px',
                ...typographyStyle['13_400'],
                color: Colors.gray700,
              }}
            >
              시스템 계정
            </Typography.Text>
          </span>
        )}
        {isCardPayment && viewStatus === 'info' && (
          <span
            style={{
              width: 'fit-content',
              padding: 0,
              borderRadius: 5,
              backgroundColor: Colors.grayOpacity07,
            }}
          >
            <Typography.Text
              style={{
                padding: '0 4px',
                ...typographyStyle['13_400'],
                color: Colors.gray700,
              }}
            >
              카드
            </Typography.Text>
          </span>
        )}
      </Flex>
    </Flex>
  );
};

const LabelManagementPanel: React.FC<Props> = ({ activeKey, viewType, setActiveItem }) => {
  const { categoryTree } = useCategoryLabelTreeStore();
  const flattenedTree = flattenTree(categoryTree ?? []).filter(
    (item) => !item.value?.contraCategoryId,
  );
  const activeItem = flattenedTree.find((item) => item.key === activeKey);
  const label = '계정';

  const panelRef = useRef<HTMLDivElement>(null);

  const { onNewCategoryClick, onPanelClose, viewStatus, namePath, keyPath, depth } =
    useLabelManage<CategoryTreeItem>({
      activeKey,
      activeItem,
      setActiveItem,
      viewType,
      flattenedTree,
    });

  useCloseWithESC({
    handleClose: onPanelClose,
  });

  if (!activeItem?.value) {
    return null;
  }

  return (
    <PanelWrapper direction="column" gap={12} ref={panelRef}>
      <PanelHeader<CategoryTreeItem>
        activeItem={activeItem}
        flattenedTree={flattenedTree}
        setActiveItem={setActiveItem}
        namePath={namePath}
        keyPath={keyPath}
        viewStatus={viewStatus}
      />
      {viewStatus === 'info' ? (
        <Info
          namePath={activeItem.namePath}
          depth={depth}
          activeChildren={activeItem.children}
          activeKey={activeItem.key}
          parentKey={activeItem.parentId}
          {...activeItem.value}
        />
      ) : (
        <NewInfo
          flattenedTree={flattenedTree}
          depth={activeItem.depth}
          onCancel={() => {
            setActiveItem(undefined);
          }}
          onSuccess={(treeKey) => {
            onPanelClose(treeKey);
          }}
          parentId={activeItem.parentId}
          {...activeItem.value}
        />
      )}
      {depth > 1 && viewStatus !== 'new' ? (
        <Flex direction="column" gap={12}>
          <LabelCreateButton onClick={onNewCategoryClick}>{`하위 ${label} 생성`}</LabelCreateButton>
          {depth > 2 && (
            <LabelRemoveButton
              onRemove={async () => {
                if (activeItem.children.length === 1 && !activeItem.children[0]?.value?.name) {
                  overlays.open(({ isOpen, onClose }) => (
                    <CategoryLabelRemoveModal
                      open={isOpen}
                      onCancel={onClose}
                      item={activeItem}
                      defaultStep={2}
                    />
                  ));
                  return;
                }
                overlays.open(({ isOpen, onClose }) => (
                  <CategoryLabelRemoveModal open={isOpen} onCancel={onClose} item={activeItem} />
                ));
              }}
            >
              <Typography.Text
                style={{
                  ...typographyStyle['14_400'],
                  color: Colors.gray700,
                }}
              >
                삭제하기
              </Typography.Text>
            </LabelRemoveButton>
          )}
        </Flex>
      ) : null}
    </PanelWrapper>
  );
};

export default LabelManagementPanel;
